import { parseJSON } from './fetch_utils';
import { logoutSuccess } from '../actions/session_actions';
import { createTakeLatest } from '../utils/TakeLatestUtils';

/**
 * One request to openwrench-intelligence-service.
 *
 * That service does not speak this app's `{ status, data }` envelope: a success
 * is the bare model and a failure is an HTTP status with
 * `{ "error": { "code", "correlation_id" } }`. So `restCrudThunksGenerator`
 * cannot be used for it — the generator decides failure by `d.status ===
 * 'error'`, which never arrives, and a 422 would fall into its success branch
 * and dispatch `fetchSuccess(undefined)`.
 *
 * What every AOP thunk then has to do is the same four things, and this is
 * them: read the body, hand a 401 to the session, turn a failure into the
 * service's error code, and otherwise pass the answer on. Fifteen call sites
 * across the three AOP thunk files wrote that out in full.
 */

export const AOP_JSON_HEADERS = {
	Accept: 'application/json',
	'Content-Type': 'application/json',
};

/** The service's error code, or the caller's fallback when the body isn't one. */
export const errorCodeOf = (body: any, fallback: string): string => {
	const code = body && body.error ? body.error.code : null;
	return typeof code === 'string' ? code : fallback;
};

/** A read superseded by a newer one. `createTakeLatest` rejects with this. */
export const isCancelledRead = (error: any): boolean => Boolean(error && error.isCancelled);

/**
 * A take-latest per named stream, for reads where only the newest answer is
 * wanted: a table's collection, or one tab's feed.
 *
 * Whether a superseded read then rejects or simply never settles is the
 * caller's to decide — `agentRequest` leaves it unsettled, because the screen
 * it would have answered has moved on; the execution feeds reject so their
 * callers can tell a takeover from a failure and leave the spinner alone.
 */
const latestByStream: { [stream: string]: (request: Promise<any>) => Promise<any> } = {};

export const throughLatest = <T>(stream: string | undefined, request: Promise<T>): Promise<T> => {
	if (!stream) {
		return request;
	}

	if (!latestByStream[stream]) {
		latestByStream[stream] = createTakeLatest();
	}

	return latestByStream[stream](request);
};

interface AOPRequestHandlers<TResult> {
	/** Reported when the body does not name a code of its own. */
	fallback: string;
	/**
	 * Tell the screen what went wrong. Called for a refusal and for a dropped
	 * connection alike — both are "the service does not have this".
	 */
	onFailure: (code: string) => void;
	/**
	 * The answer, once it is known to be one.
	 *
	 * Throwing from here reports the thrown message as the code, which is how a
	 * caller rejects a body that came back in a shape it cannot read.
	 */
	onSuccess: (json: any) => TResult;
}

/**
 * Runs `request` and settles on what the service said.
 *
 * `request` is passed in rather than built here so a caller can wrap it first —
 * the list reads go through a take-latest, and a superseded one rejects with
 * `isCancelled`. That leaves this promise unsettled on purpose: the caller has
 * no answer to give, and its `then` describes a page nobody is drawing.
 */
export const agentRequest = <TResult>(
	dispatch: (action: any) => void,
	request: Promise<any>,
	handlers: AOPRequestHandlers<TResult>
): Promise<TResult> =>
	new Promise<TResult>((resolve, reject) => {
		request
			.then(parseJSON)
			.then(({ json, status, ok }) => {
				if (status === 401) {
					dispatch(logoutSuccess());
					reject('unauthorized');
					return;
				}

				if (!ok) {
					const code = errorCodeOf(json, handlers.fallback);
					handlers.onFailure(code);
					reject(code);
					return;
				}

				resolve(handlers.onSuccess(json));
			})
			.catch((error) => {
				if (isCancelledRead(error)) {
					return;
				}

				const code = error && error.message ? error.message : handlers.fallback;
				handlers.onFailure(code);
				reject(code);
			});
	});
